import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ClipboardList, Laptop, Bookmark, Users, Download, BellRing, Trash2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { api, errMsg, useFetch, fmtDate, label, money } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, btn, Loading, ErrorBox, EmptyState, StatusBadge, TextArea } from "../components/kit";
import { JobCard, ProjectCard, GovCard } from "../components/cards";

export async function downloadResume(appId, name) {
  try {
    const { data } = await api.get(`/applications/${appId}/resume`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = Object.assign(document.createElement("a"), { href: url, download: name || "resume" });
    a.click();
    toast.success("Resume downloaded");
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) {
    if (e.response?.data instanceof Blob) {
      try { const message = JSON.parse(await e.response.data.text()).detail; toast.error(message || "Could not download the resume."); return; } catch { /* Non-JSON upstream error. */ }
    }
    toast.error("Could not download the resume. Please try again.");
  }
}

const Wrap = ({ title, desc, children }) => <><PageHeader eyebrow="Dashboard" title={title} description={desc}><Link to="/dashboard" className={btn("secondary")}>Back to dashboard</Link></PageHeader><div className="container-x py-8">{children}</div></>;
const State = ({ r, children }) => r.loading ? <Loading /> : r.error ? <ErrorBox message={r.error} onRetry={r.reload} /> : children(r.data);

export function MyApplications() {
  const r = useFetch("/me/applications");
  const withdraw = async (a) => {
    if (!window.confirm(`Withdraw your application for ${a.job_title}? This cannot be undone.`)) return;
    try { await api.post(`/applications/${a.id}/withdraw`); toast.success("Application withdrawn"); r.reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Wrap title="My applications" desc="Track every application and its status history.">
      <State r={r}>{(apps) => apps.length ? (
        <ul className="space-y-4" data-testid="applications-list">
          {apps.map((a) => (
            <li key={a.id} className="card p-5" data-testid={`application-${a.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div><h2 className="text-xl font-bold"><Link to={`/jobs/${a.job_id}`} className="hover:underline">{a.job_title}</Link></h2><p className="text-sub">{a.company_name} · Updated {fmtDate(a.updated_at)}</p></div>
                <StatusBadge status={a.status} />
              </div>
              <details className="mt-3"><summary className="cursor-pointer min-h-[44px] flex items-center font-semibold text-link">Status history</summary>
                <ol className="mt-2 space-y-1 text-sm">{a.history?.map((h, i) => <li key={i}>{fmtDate(h.at)} — {label(h.status)} ({h.by}){h.note && `: ${h.note}`}</li>)}</ol>
              </details>
              <div className="mt-3 flex flex-wrap gap-2">
                {a.resume_name && <button className={btn("secondary", "sm")} data-testid={`my-resume-${a.id}`} onClick={() => downloadResume(a.id, a.resume_name)}><Download className="h-4 w-4" aria-hidden="true" />My CV</button>}
                {a.status === "draft" && <Link to={`/jobs/${a.job_id}`} className={btn("primary", "sm")}>Finish application</Link>}
                {!["withdrawn", "rejected", "offered"].includes(a.status) && <button className={btn("danger", "sm")} onClick={() => withdraw(a)} data-testid={`withdraw-${a.id}`}>Withdraw</button>}
              </div>
            </li>
          ))}
        </ul>
      ) : <EmptyState icon={ClipboardList} title="No applications yet" body="When you apply for jobs, you'll be able to track them here." action={<Link to="/jobs" className={btn()}>Browse jobs</Link>} />}</State>
    </Wrap>
  );
}

export function MyProposals() {
  const r = useFetch("/me/proposals");
  const withdraw = async (p) => {
    if (!window.confirm(`Withdraw your proposal for ${p.project_title}?`)) return;
    try { await api.put(`/proposals/${p.id}/status`, { status: "withdrawn" }); toast.success("Proposal withdrawn"); r.reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Wrap title="My proposals" desc="Proposals you've sent to clients.">
      <State r={r}>{(items) => items.length ? (
        <ul className="space-y-4" data-testid="proposals-list">
          {items.map((p) => (
            <li key={p.id} className="card p-5 flex flex-wrap items-start justify-between gap-3" data-testid={`proposal-${p.id}`}>
              <div><h2 className="text-xl font-bold"><Link to={`/freelancing/${p.project_id}`} className="hover:underline">{p.project_title}</Link></h2><p className="text-sub">{money(p.amount, null, p.currency)} · {p.timeline} · Sent {fmtDate(p.created_at)}</p></div>
              <div className="flex flex-wrap items-center gap-2"><StatusBadge status={p.status} />{["submitted", "shortlisted"].includes(p.status) && <button className={btn("danger", "sm")} onClick={() => withdraw(p)} data-testid={`withdraw-proposal-${p.id}`}>Withdraw</button>}</div>
            </li>
          ))}
        </ul>
      ) : <EmptyState icon={Laptop} title="No proposals yet" body="Find a project that suits your skills and send your first proposal." action={<Link to="/freelancing" className={btn()}>Browse projects</Link>} />}</State>
    </Wrap>
  );
}

function SavedSearches() {
  const r = useFetch("/me/saved-searches");
  const del = async (s) => {
    if (!window.confirm(`Delete the saved search “${s.name}”? You'll stop getting alerts for it.`)) return;
    try { await api.delete(`/me/saved-searches/${s.id}`); toast.success("Saved search deleted"); r.reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const setFreq = async (s, email_freq) => {
    try { await api.patch(`/me/saved-searches/${s.id}`, { email_freq }); toast.success("Email alert setting updated"); r.reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const [previewing, setPreviewing] = useState(false);
  const preview = async () => {
    setPreviewing(true);
    try { const { data } = await api.post("/me/saved-searches/preview-digest"); toast.success(data.message); } catch (e) { toast.error(errMsg(e)); } finally { setPreviewing(false); }
  };
  return (
    <section className="mb-10" aria-labelledby="ss-h" data-testid="saved-searches">
      <h2 id="ss-h" className="flex items-center gap-2 text-2xl font-bold"><BellRing className="h-5 w-5" aria-hidden="true" />Saved searches & alerts</h2>
      <p className="mt-1 text-sub">You always get an in-app notification when a newly approved listing matches. For each search, choose email alerts: a daily digest, a weekly digest, instantly per match, or off. Turn all opportunity alerts off under Settings → Notifications.</p>
      <button className={`${btn("secondary", "sm")} mt-3`} onClick={preview} disabled={previewing} data-testid="preview-digest-btn">{previewing ? "Sending…" : "Send me a test digest"}</button>
      <div className="mt-4">
        <State r={r}>{(items) => items.length ? (
          <ul className="grid gap-3 md:grid-cols-2">{items.map((s) => (
            <li key={s.id} className="card p-4 flex flex-col gap-3" data-testid={`saved-search-${s.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div><p className="font-bold">{s.name}</p><p className="text-sm text-sub">Saved {fmtDate(s.created_at)} · {s.match_count} alert(s) sent{s.last_notified_at && ` · last ${fmtDate(s.last_notified_at)}`}</p></div>
                <div className="flex gap-2"><Link to={s.url} className={btn("primary", "sm")} data-testid={`run-saved-search-${s.id}`}>View results</Link><button className={btn("ghost", "sm")} onClick={() => del(s)} aria-label={`Delete saved search ${s.name}`} data-testid={`delete-saved-search-${s.id}`}><Trash2 className="h-4 w-4" aria-hidden="true" /></button></div>
              </div>
              <div className="flex items-center gap-2">
                <label htmlFor={`freq-${s.id}`} className="text-sm font-semibold text-sub">Email alerts</label>
                <select id={`freq-${s.id}`} value={s.email_freq || "daily"} onChange={(e) => setFreq(s, e.target.value)} className="min-h-[44px] rounded-lg border border-field bg-white px-2 text-sm" data-testid={`saved-search-freq-${s.id}`}>
                  <option value="daily">Daily digest</option>
                  <option value="weekly">Weekly digest</option>
                  <option value="instant">Instant</option>
                  <option value="off">Off</option>
                </select>
              </div>
            </li>
          ))}</ul>
        ) : <p className="rounded-lg bg-surface p-4 text-sub">No saved searches yet. Use “Save search & get alerts” on the Jobs, Freelancing or Government pages.</p>}</State>
      </div>
    </section>
  );
}

export function Saved() {
  const r = useFetch("/me/saved");
  const tabs = [["job", "Jobs"], ["project", "Projects"], ["gov", "Government"], ["post", "Resources & posts"], ["recent", "Recently viewed jobs"]];
  return (
    <Wrap title="Saved items" desc="Saved searches, jobs, projects, government listings and community resources.">
      <SavedSearches />
      <State r={r}>{(d) => (
        <Tabs defaultValue="job">
          <TabsList className="h-auto flex-wrap gap-1 bg-mist p-1">{tabs.map(([v, l]) => <TabsTrigger key={v} value={v} className="min-h-[44px] px-4 text-base data-[state=active]:bg-white text-ink" data-testid={`saved-tab-${v}`}>{l} ({d[v].length})</TabsTrigger>)}</TabsList>
          {tabs.map(([v, l]) => (
            <TabsContent key={v} value={v} className="mt-6">
              {d[v].length ? <ul className="grid gap-4 md:grid-cols-2">{d[v].map((it) => <li key={it.id} className="flex [&>*]:w-full">
                {v === "job" || v === "recent" ? <JobCard job={it} saved={v === "job" ? true : undefined} /> : v === "project" ? <ProjectCard p={it} saved /> : v === "gov" ? <GovCard g={it} /> : <div className="card p-5"><h3 className="text-lg font-bold"><Link to={`/community/posts/${it.id}`} className="hover:underline">{it.title}</Link></h3><p className="text-sm text-sub">{it.category}</p></div>}
              </li>)}</ul> : <EmptyState icon={Bookmark} title={`No ${l.toLowerCase()} yet`} body="Use the Save button on any listing to keep it here." />}
            </TabsContent>
          ))}
        </Tabs>
      )}</State>
    </Wrap>
  );
}

function Respond({ item, path, onDone }) {
  const [note, setNote] = useState("");
  const act = async (status) => {
    try { await api.put(`${path}/${item.id}`, { status, response: note }); toast.success(`Request ${status}`); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-3 space-y-2">
      <TextArea id={`respond-${item.id}`} label="Response note" rows={2} hint="For example, how to arrange the session." value={note} onChange={(e) => setNote(e.target.value)} />
      <div className="flex gap-2"><button className={btn("primary", "sm")} onClick={() => act("accepted")} data-testid={`accept-${item.id}`}>Accept</button><button className={btn("danger", "sm")} onClick={() => act("declined")} data-testid={`decline-${item.id}`}>Decline</button></div>
    </div>
  );
}

export function Mentorship() {
  const { user } = useAuth();
  const r = useFetch("/me/mentorship");
  const cancel = async (m) => { try { await api.put(`/mentorship/${m.id}`, { status: "cancelled" }); r.reload(); } catch (e) { toast.error(errMsg(e)); } };
  const Item = ({ m, kind }) => (
    <li className="card p-5" data-testid={`mentorship-${m.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div><p className="font-bold">{label(m.type || m.kind)} {kind === "sent" ? `to ${m.professional_name || "member"}` : `from ${m.requester_name || m.from_name}`}</p><p className="text-sm text-sub">{fmtDate(m.created_at)}{m.company && ` · ${m.company}`}{m.role_title && ` · ${m.role_title}`}</p></div>
        <StatusBadge status={m.status} />
      </div>
      <p className="mt-2 whitespace-pre-line">{m.message}</p>
      {m.response && <p className="mt-2 rounded-lg bg-surface p-3"><strong>Response: </strong>{m.response}</p>}
      {kind === "received" && m.status === "pending" && <Respond item={m} path={m.kind ? "/referrals" : "/mentorship"} onDone={r.reload} />}
      {kind === "sent" && !m.kind && m.status === "pending" && m.requester_id === user.id && <button className={`${btn("secondary", "sm")} mt-3`} onClick={() => cancel(m)}>Cancel request</button>}
    </li>
  );
  const Sec = ({ title, items, kind }) => <section className="mt-8"><h2 className="text-2xl font-bold">{title}</h2>{items.length ? <ul className="mt-4 space-y-4">{items.map((m) => <Item key={m.id} m={m} kind={kind} />)}</ul> : <p className="mt-3 text-sub">Nothing here yet.</p>}</section>;
  return (
    <Wrap title="Mentorship & referrals" desc="Requests you've sent and received. Scheduling and messaging happen outside OpenPath for now.">
      <State r={r}>{(d) => (d.sent.length + d.received.length + d.referrals_sent.length + d.referrals_received.length) ? <>
        <Sec title="Requests received" items={d.received} kind="received" />
        <Sec title="Referral & introduction requests received" items={d.referrals_received} kind="received" />
        <Sec title="Requests you sent" items={d.sent} kind="sent" />
        <Sec title="Referral requests you sent" items={d.referrals_sent} kind="sent" />
      </> : <EmptyState icon={Users} title="No requests yet" body="Find a mentor, coach or counsellor in the professional directory." action={<Link to="/community/directory" className={btn("community")}>Open directory</Link>} />}</State>
    </Wrap>
  );
}
